/* eslint-disable dot-notation */
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Badge,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
// ICONS
import InboxIcon from '@mui/icons-material/Inbox';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import HelpCenterOutlinedIcon from '@mui/icons-material/HelpCenterOutlined';

// CUSTON COMPONENTS
import { AlarmOutlined, StopCircleOutlined, TimerOutlined } from '@mui/icons-material';

import FeedbackAlert, { FeedbackAlertType } from '../../../../components/FeedbackAlert';
import { useUrlQuery } from '../../../../hooks/useUrlQuery';

import api from '../../../../services/api';
import { OpOsQueue } from '../queue';

// type ViewType = 'dashboard' | 'queue' | 'attendance' | 'management' | 'report';

type FilterType = {
  text: string;
  key: string;
  disabled: boolean;
  icon: ReactNode;
};
type ListType = {
  text: string;
  key: string;
  disabled: boolean;
  icon: ReactNode;
  filters: FilterType[];
  count?: number;
};

type RulesType = {
  value: string;
  allow: string;
};

type PermissionsType = {
  manager: boolean;
};

let lastPermition: PermissionsType = {
  manager: false,
};

function ListOption(props: { icon: ReactNode; text: string; badgeContent?: number }) {
  const theme = useTheme();
  const widthSmLess = useMediaQuery(theme.breakpoints.down('sm'));
  const { icon, text, badgeContent = 0 } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Badge
        color="warning"
        badgeContent={badgeContent}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {icon}
      </Badge>

      <Typography sx={{ display: widthSmLess ? 'none' : 'flex' }}>{text}</Typography>
    </Box>
  );
}

export default function OpOsHomePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const urlQuery = useUrlQuery();

  const currentUrlView = urlQuery.get('view') ?? 'dashboard';
  const currentUrlFilter = urlQuery.get('filter') ?? 'started';
  const currentUrlPage = urlQuery.get('page') ?? '1';

  const widthSmLess = useMediaQuery(theme.breakpoints.down('sm'));

  const [permissions, setPermissions] = useState<PermissionsType>(lastPermition);
  const [badge, setBadge] = useState<{ teste: number }>({ teste: 15 });

  const viewList: ListType[] = [
    {
      key: 'dashboard',
      text: 'Dashboard',
      disabled: false,
      icon: <DashboardOutlinedIcon />,
      filters: [],
    },
    {
      key: 'queue',
      text: 'FILA DE O.P./O.S',
      disabled: false,
      icon: <AssignmentLateOutlinedIcon />,
      filters: [],
    },
    {
      key: 'running',
      text: 'O.P./O.S EM PRODUÇÃO',
      disabled: false,
      icon: <AssignmentTurnedInOutlinedIcon />,
      filters: [],
    },
    {
      key: 'search',
      text: 'Pesquisar',
      disabled: false,
      icon: <ContentPasteSearchOutlinedIcon />,
      filters: [],
    },
  ];
  const [fetching, setFetching] = useState(false);

  const selectedView = viewList.find(v => v.key === currentUrlView);
  const selectedFilter = selectedView?.filters?.find(v => v.key === currentUrlFilter);

  const [openNewHelpdeskDialog, setOpenNewHelpdeskDialog] = useState(false);

  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [feedbackType, setFeedbackType] = useState<FeedbackAlertType>('success');

  const checkPermition = async () => {
    if (fetching) return;
    setFetching(true);
    try {
      const { data } = await api.get(`intranet/users/my`, {
        params: {},
      });
      const rules: RulesType[] = data?.rules ?? [];

      const managerRule = rules.find(r => r.value === 'erp/engineering/os/manager') as RulesType;

      const manager = managerRule?.allow === '1';

      setPermissions({
        manager,
      });
      lastPermition = {
        manager,
      };
    } catch (error) {
      const { data: response } = error.response;
    }
    setFetching(false);
  };

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, nextView: string) => {
    if (nextView !== null) {
      let filter = 'all';
      if (nextView === 'attendance') filter = 'started';
      else if (nextView === 'dashboard') filter = 'started';
      const params = new URLSearchParams({ view: nextView, filter });
      navigate({ search: params.toString() });
    }
  };

  const renderMenu = useMemo(
    () => (
      <Paper sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1, flex: 1 }}>
        {widthSmLess && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
            <Typography>{selectedView?.text?.toUpperCase()}</Typography>
          </Box>
        )}
        <ToggleButtonGroup
          orientation="horizontal"
          value={selectedView?.key}
          exclusive
          onChange={handleViewChange}
          fullWidth
        >
          {viewList?.map((v, i) => {
            const { icon, text } = v;
            const key = `${v.key}-${i}`;
            const disabled = v.key === 'management' ? !permissions?.manager : v.disabled;
            return (
              <ToggleButton value={v.key} aria-label="list" key={key} color="info" fullWidth disabled={disabled}>
                <ListOption text={text} icon={icon} badgeContent={badge[v.key]} />
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Paper>
    ),
    [selectedView, widthSmLess, permissions, badge, viewList],
  );

  const renderContent = useMemo(
    () => (
      <>
        {currentUrlView === 'dashboard' && null}
        {currentUrlView === 'attendance' && null}
        {currentUrlView === 'search' && null}
        {currentUrlView === 'management' && null}
        {currentUrlView === 'report' && null}
        {currentUrlView === 'queue' && <OpOsQueue />}
      </>
    ),
    [currentUrlView, currentUrlFilter],
  );

  useEffect(() => {
    checkPermition();
  }, []);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', flex: 1, width: '100%' }}>{renderMenu}</Box>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          {renderContent}
        </Grid>
      </Grid>

      <FeedbackAlert message={feedbackMsg} show={feedbackMsg?.length > 0} type={feedbackType} />
    </Box>
  );
}
